<script setup>
import {ref, computed, nextTick, onBeforeUnmount, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import { t } from "@/controllerLK/GlobusLocalization";
import {useProjects} from "@/store/projects";
import {createProjectForm} from "@/composables/constans/formData";
import {useStore} from "@/store";
import ProjectModalHeader from "@/pages/LKPage/LKWindow/LKProjectWindow/ProjectModal/ProjectModalHeader.vue";
import AccessListModal from "@/pages/LKPage/LKWindow/LKProjectWindow/ProjectModal/AccessListModal.vue";
import ProjectHistory from "@/pages/LKPage/LKWindow/LKProjectWindow/ProjectModal/ProjectHistory.vue";
import TabsMenu from "@/components/common/TabsMenu.vue";
import ProjectMainTab from "@/pages/LKPage/LKWindow/LKProjectWindow/ProjectModal/ProjectMainTab.vue";
import TelegramNotifications from "@/pages/LKPage/LKWindow/LKProjectWindow/ProjectModal/TelegramNotifications.vue";
import {
  sendTimeOptions,
  useTelegramRules, verifySubscription
} from "@/pages/LKPage/LKWindow/LKProjectWindow/ProjectModal/TelegramNotifications/useTelegramRules.js";
import LocalLoader from "@/components/common/Preloader/LocalLoader.vue";


const emit = defineEmits(["closeModal", "updateProjectList", "showDeleteProjectModal"]);

// router
const route = useRoute();
const router = useRouter();

// stores
const projectStore = useProjects();
const mainStore = useStore();

// ID Текущего проекта.
const projectId = computed(() => route.params.projectId || null);

// TODO: Заглушка является ли страница архивной. Больше не используется.
const isArchivePage = false;

// Название текущего проекта.
const getProjectName = computed(() => defaultProject.value?.name ?? t("project_modal_header.title"));

// Начальное состояния объекта проекта.
const defaultProject = ref(null);

// Объект проекта для редактирования.
const createProjectFormData = ref(JSON.parse(JSON.stringify(createProjectForm)));

const isProjectLoading = ref(false);

// Telegram подписки
const {
  initTelegramRules,
  verified: telegramVerified,
  rules: telegramRules,
  isLoading: telegramLoading,
  availableTopics,
  topicSearchQuery,
  filteredTopics,
  verifySubscriptionRecursively,
  recursivelyVerifying,
  stopRecursiveVerification,
  saveAllRules,
  deleteMarkedRules,
  resetDeletedRules,
  addRule,
  deleteRule,
  toggleImportance,
  toggleTonality,
  toggleTopic,
  toggleAllTopics,
  validateRules,
  hasErrors: telegramHasErrors
} = useTelegramRules(projectId.value);

// Тип модального окна. Создание/Редактирование
const isEditProject = computed(() => route.params.projectId && route.matched[2]?.name === "project-edit");

// Начальное состояние telegram-правил для отслеживания изменений
const initialTelegramRules = ref([]);

// Список ошибок по проекту.
const errorList = ref([]);

// Общая функция загрузки данных по проекту.
const loadProjectData = async () => {
  isProjectLoading.value = true;

  // Повторная проверка подписки бота, если пользователь не авторизован.
  if (!mainStore.isTelegramAuthorized) {
    await verifySubscription();
  }

  const [project] = await Promise.all([
    projectStore.getProject(projectId.value),
    initTelegramRules(projectId.value)
  ]);
  isProjectLoading.value = false;

  return project;
};

// Инициализация формы для редактирования
const initializeEditMode = (project) => {
  defaultProject.value = project;
  createProjectFormData.value = JSON.parse(JSON.stringify(project));

  if (telegramVerified.value) {
    initialTelegramRules.value = JSON.parse(JSON.stringify(telegramRules.value));
  }
};

// Инициализация формы для создания
const initializeCreateMode = () => {
  defaultProject.value = null;
  createProjectFormData.value = JSON.parse(JSON.stringify(createProjectForm));
  initialTelegramRules.value = [];
  availableTopics.value = [];
};

watch([projectId, isEditProject], async ([newProjectId, isEdit]) => {
  errorList.value = [];

  const project = newProjectId && isEdit && await loadProjectData();

  if (project) {
    initializeEditMode(project);
  } else if (newProjectId && isEdit) {
    // Проект не найден — редирект на список
    await router.replace("/lk/projects");
  } else {
    initializeCreateMode();
  }

  // Установка фокуса
  await nextTick();
  if (activeTab.value === "main" && currentTabRef.value?.focus) {
    currentTabRef.value.focus();
  }
}, { immediate: true });

onBeforeUnmount(() => {
  stopRecursiveVerification();
});

// Список вкладок окна проекта.
const tabsList = [
  { label: t("lk_project_modal.tabs.main"), value: "main", component: ProjectMainTab },
  { label: t("lk_project_modal.tabs.telegram"), value: "telegramm", component: TelegramNotifications}
];
// Активная компонент вкладки проекта
const currentTabComponent = computed(() => {
  return tabsList.find(tab => tab.value === activeTab.value)?.component;
});
// Наименование активной вкладки проекта (синхронизация с URL query параметром)
const activeTab = computed({
  get: () => {
    const tab = route.query.tab;
    // Проверяем, что значение валидное
    if (tab && tabsList.some(t => t.value === tab)) {
      return tab;
    }
    return "main";
  },
  set: (value) => {
    router.replace({ query: { ...route.query, tab: value } });
  }
});
// Ref на компонент активного таба проекта.
const currentTabRef = ref(null);

// Модальное окно истории изменений проекта.
const isShowProjectHistory = ref(false);
const toggleProjectHistory = () => { isShowProjectHistory.value = !isShowProjectHistory.value; };

// Модальное окно управления доступом к проекту.
const isShowAccessList = ref(false);
const toggleAccessList = () => { isShowAccessList.value = !isShowAccessList.value; };

// Добавление прав доступа пользователю проекта
const addUserList = (userList) => {
  createProjectFormData.value.users = userList.map((user) => user.userId);
};

// Проверка есть ли несохраненные изменения в проекте / ТГ подписках.
// Вызывается при закрытие окна проекта.
const isHasUnsavedChanges = computed(() => {
  if (!isEditProject.value) {
    return createProjectFormData.value.name.trim().length > 0;
  }

  const formChanged = JSON.stringify(createProjectFormData.value) !== JSON.stringify(defaultProject.value);
  const telegramChanged = JSON.stringify(telegramRules.value) !== JSON.stringify(initialTelegramRules.value);

  return formChanged || telegramChanged;
});

// Отображение модального окна наличия несохраненных изменений
const isShowUnsavedChangesDialog = ref(false);


// Флаг отправки формы сохранения проекта.
const isFormSubmitted = ref(false);

const createProject = async () => {

  const projectFormData = JSON.parse(JSON.stringify(createProjectFormData.value));

  isFormSubmitted.value = true;
  errorList.value = [];
  projectFormData.name = projectFormData.name.trim();
  if (projectFormData.name.length === 0) {
    errorList.value.push("noName");
  }

  // eslint-disable-next-line
  const emojiRegEx = new RegExp(/[^\x00-\x7Fа-яА-Я]/g)

  if (projectFormData.name.search(emojiRegEx) !== -1) {
    errorList.value.push("noEmoji");
  }

  const moduleList = projectFormData.modules;
  if (moduleList.length === 0) {
    errorList.value.push("noModule");
  }

  if (moduleList.length === 0) {
    errorList.value.push("noModule");
  }

  // Валидация правил ТГ подписок.
  if (!validateRules()) {
    errorList.value.push("rulesError");
  }

  if (!errorList.value.length > 0) {
    mainStore.loader = true;
    if (!isEditProject.value) {
      const newProject = await projectStore.createProject(projectFormData);
      projectFormData.id = newProject.id;
    } else {
      await projectStore.updateProject(projectFormData);
    }

    // Пользователи проекта
    await projectStore.updateAccessToProject(projectFormData.id, createProjectFormData.value.users);

    // Создание/Обновление/Удаление Telegram подписок
    if (telegramVerified.value && telegramRules.value.length > 0) {
      const saveSuccess = await saveAllRules(projectFormData.id.toString());
      const deleteSuccess = await deleteMarkedRules();

      if (!saveSuccess || !deleteSuccess) {
        mainStore.loader = false;
        isFormSubmitted.value = false;
        return;
      }
    }

    emit("closeModal");
    await projectStore.getProjects();

    if (!projectFormData.is_archive) {
      await projectStore.setActiveProject(projectFormData.id);
    }

  }
  mainStore.loader = false;
  isFormSubmitted.value = false;
};

// Закрытие модального окна редактирования проекта
const closeProjectModal = () => {
  if (isEditProject.value) {
    if (isHasUnsavedChanges.value) {
      isShowUnsavedChangesDialog.value = true;
    } else {
      emit("closeModal");
    }
  } else {
    emit("closeModal");
  }
};

const getNameAlert = computed(() => {
  if (errorList.value.find((i) => i === "noName")) {
    return t("project_modal_name.error.required");
  } else if (errorList.value.find((i) => i === "noEmoji")) {
   return t("project_modal_name.error.emoji");
  } else {
    return "";
  }
});

// Обработчик выбора частоты получения отчета.
const handleSetFrequency = (rule, frequency) => {
  rule.frequency = frequency;
  // Если выбран параметр "Сразу".
  if (frequency === "immediately") {
    rule.sendTime = [];
    rule.weekday = null;
    rule.timezone = "UTC";
  // Если выбран параметр "Каждую неделю"
  } else if (frequency === "weekly") {
    rule.sendTime = sendTimeOptions.weekly[0].value;
    rule.sendTime = ["6:00"];
    rule.weekday = "monday";
    rule.timezone = "UTC";
    // Если выбран параметр "Каждые 3 часа"
  } else if (frequency === "every_3h") {
    rule.sendTime = sendTimeOptions.every_3h[0].value;
    rule.weekday = null;
    rule.timezone = "UTC";
    // Если выбран параметр "Каждые 6 часов"
  } else if (frequency === "every_6h") {
    rule.sendTime = sendTimeOptions.every_6h[0].value;
    rule.weekday = null;
    rule.timezone = "UTC";
    // Если выбран параметр "Каждые 24 часа"
  } else if (frequency === "every_24h") {
    rule.sendTime = sendTimeOptions.every_24h[0].value;
    rule.weekday = null;
    rule.timezone = "UTC";
  }
};

const handleSetSendTime = (rule, time) => {
  rule.sendTime = time;
};

const handleSetTimezone = (rule, tz) => {
  rule.timezone = tz;
};

const handleSetWeekday = (rule, weekday) => {
  rule.weekday = weekday;
};
</script>

<template>
  <div
    :key="projectId"
    class="modal-create-project"
  >
    <ProjectModalHeader
      :title="getProjectName"
      @close-modal="closeProjectModal"
    />

    <TabsMenu
      v-model="activeTab"
      :tabs="tabsList"
    />

    <!-- Лоадер на загрузку информации о проекте -->
    <div
      v-if="isProjectLoading"
      class="loading-overlay"
    >
      <LocalLoader color="#DEDEDE" />
      <span>{{ t('telegram_subscription.loading') }}</span>
    </div>

    <KeepAlive v-else>
      <Component
        :is="currentTabComponent"
        :key="activeTab"
        ref="currentTabRef"
        v-model="createProjectFormData"
        :is-edit="isEditProject"
        :is-archive="isArchivePage"
        :is-form-submitted="isFormSubmitted"
        :is-has-unsaved-changes="isHasUnsavedChanges"
        :name-alert="getNameAlert"
        :project-id="createProjectFormData.id"
        :rubrics="createProjectFormData.rubrics || []"
        :verified="telegramVerified"
        :rules="telegramRules"
        :is-loading="telegramLoading"
        :available-topics="availableTopics"
        :topic-search-query="topicSearchQuery"
        :filtered-topics="filteredTopics"
        :recursively-verifying="recursivelyVerifying"
        @close="closeProjectModal"
        @show-delete-project-modal="emit('showDeleteProjectModal')"
        @toggle-access-list="toggleAccessList"
        @toggle-project-history="toggleProjectHistory"
        @submit="createProject"
        @update:topic-search-query="val => topicSearchQuery = val"
        @verify-subscription="verifySubscriptionRecursively"
        @stop-verification="stopRecursiveVerification"
        @add-rule="addRule"
        @delete-rule="deleteRule"
        @toggle-importance="toggleImportance"
        @toggle-tonality="toggleTonality"
        @toggle-topic="toggleTopic"
        @toggle-all-topics="toggleAllTopics"
        @set-frequency="handleSetFrequency"
        @set-send-time="handleSetSendTime"
        @set-weekday="handleSetWeekday"
        @set-timezone="handleSetTimezone"
      />
    </KeepAlive>

    <div class="modal-create-project__control">
      <ButtonAccentPrimary
        :pressed="isFormSubmitted"
        :disabled="isProjectLoading || !isHasUnsavedChanges || telegramHasErrors"
        size="large"
        class="modal-create-project__button"
        @click="createProject"
      >
        <span v-if="!isEditProject">{{ t('project_modal_creating.button.create') }}</span>
        <span v-else>{{ t('project_modal_creating.button.save') }}</span>
      </ButtonAccentPrimary>
      <ButtonNeutralSecondary
        size="large"
        class="modal-create-project__button"
        @click="closeProjectModal"
      >
        {{ t('project_modal_creating.button.cancel') }}
      </ButtonNeutralSecondary>
    </div>

    <Modal
      v-if="isShowUnsavedChangesDialog"
      width="315px"
      :cancel-text="t('lk_project_modal.unsaved_changes.button.cancel')"
      :confirm-text="t('lk_project_modal.unsaved_changes.button.save')"
      class="unsaved-changed-dialog"
      :title="t('lk_project_modal.unsaved_changes.title')"
      teleport="#appMain"
      :use-cross-icon="false"
      @confirm="resetDeletedRules(); emit('closeModal')"
      @cancel="isShowUnsavedChangesDialog = false;"
    >
      <div class="unsaved-changed-dialog__text">
        {{ t('lk_project_modal.unsaved_changes.message') }}
      </div>
    </Modal>

    <AccessListModal
      v-if="isShowAccessList"
      :project="createProjectFormData"
      :is-edit="isEditProject"
      :user-list="createProjectFormData.users"
      :show-user-list="isShowAccessList"
      @add-user-list="val => addUserList(val)"
      @close-modal="isShowAccessList = false"
    />

    <Modal
      v-if="isShowProjectHistory"
      width="1110px"
      :use-default-body="false"
      teleport="#appMain"
      :title="t('project_history_modal.title')"
      :use-cross-icon="true"
      :use-confirm="false"
      :use-cancel="false"
      @close="isShowProjectHistory = false;"
    >
      <ProjectHistory />
    </Modal>
  </div>
</template>
<style lang="scss" scoped>

.unsaved-changed-dialog {
  z-index: 2001;

  &__text {
    color: #373D3F;
  }
}

.modal-create-project {
  position: fixed;
  z-index: 2001;
  top: 0;
  right: 0;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: start;
  width: 600px;
  height: 100%;
  padding: 20px;
  background: #FFFFFF;
  box-shadow: 0 4px 10px 0 #00000024;
  gap: 20px;

  &__control {
    display: flex;
    gap: 8px;
    margin-top: auto;
  }

  &__button {
    width: 133px;
  }
}

.loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 20px;
  z-index: 10;
  height: 100%;

  span {
    color: var(--gray-gray60);
    font-size: 14px;
    font-weight: 500;
  }
}

</style>
